import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {ModalsContext} from '../../modals/ModalsContext';
import {MODALS_TYPES} from '../../../../constants/ModalsConstants';
import {EventModalProps} from "../../../../@types/inputs";
import {GetLiveEventCall} from "../../../../apis/live";
import {ModalLiveTable} from "../ModalLiveTable";
import {WebSocketContext} from "../../common/websocket/WebSocketContext";
import {toLocalDate} from "../../../../store/utils/commonUtils";
import {getBetfairRefs} from "../../../../store/selectors/getBetfairRefs";
import {BetfairRefsObject} from "../../../../@types/response";

const EventLiveDetailsModal: React.FC = () => {
    const {modalsData, openedModals, setOpenedModalsCallback} = useContext(ModalsContext);
    const {socket} = useContext(WebSocketContext);
    const opening = openedModals.indexOf(MODALS_TYPES.EVENT_LIVE_DETAILS_MODAL) !== -1
    const onCancelClickCb = useCallback(() => {
        setOpenedModalsCallback(MODALS_TYPES.EVENT_LIVE_DETAILS_MODAL, false);
    }, [setOpenedModalsCallback]);

    const event: EventModalProps = (modalsData[MODALS_TYPES.EVENT_LIVE_DETAILS_MODAL] || {}).event;
    const betfairRefs: BetfairRefsObject = useSelector(getBetfairRefs);
    const [books, setBooks] = useState<any>();
    const [highlightBest, setHighlightBest] = useState<boolean>(true);

    const escFunction = useCallback((event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            setOpenedModalsCallback(MODALS_TYPES.EVENT_LIVE_DETAILS_MODAL, false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener("keydown", escFunction, false);

        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    useEffect(() => {
        if (!opening) {
            setBooks(undefined);
            return;
        }
        if (!event?.id) return;
        let active = true;
        GetLiveEventCall({eventId: event.id}).then((result) => {
            if (active) setBooks(result)
        })
        return () => {
            active = false;
        }
    }, [opening, event?.id])

    useEffect(() => {
        if (!opening || !socket || !event?.id) return;
        const handler = (liveUpdate: any) => {
            if (liveUpdate?._id !== event.id) return;
            setBooks((prev: any) => ({
                ...(prev ?? {}),
                ...liveUpdate,
                bookmakers: liveUpdate.bookmakers ?? prev?.bookmakers ?? {},
            }));
        }
        socket.on("live", handler);
        return () => {
            socket.off("live", handler);
        }
    }, [opening, socket, event?.id])

    const startDt = toLocalDate(books?.date ?? event?.date)
    const datePart = startDt ?
            `${[startDt.getDate().toString().padStart(2, '0'), (startDt.getMonth() + 1).toString().padStart(2, '0'), (startDt.getFullYear()).toString().padStart(2, '0')].join('/')}`
            : '--/--/----'
    const timePart = startDt ?
            `${startDt.getHours().toString().padStart(2, '0')}:${startDt.getMinutes().toString().padStart(2, '0')}`
            : '--:--'

    const score = (books?.score ?? event?.score ?? '-').toString().trim() || '-';
    const minuteValue = books?.minute ?? event?.minute;
    const minute = Number.isFinite(minuteValue) ? `${minuteValue}'` : '-';
    return (
            (openedModals.indexOf(MODALS_TYPES.EVENT_LIVE_DETAILS_MODAL) > -1 && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
                         onClick={(e) => {
                             if (e.target === e.currentTarget) onCancelClickCb()
                         }}>
                        <div className="relative w-full max-w-[1100px] overflow-hidden rounded-3xl border-solid border-eventDetail-secondary border-2 ring-1 ring-sky-500/30 bg-slate-950 text-white shadow-2xl">
                            <button
                                    type="button"
                                    className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-sky-500/60 text-sky-400 hover:bg-sky-500/10"
                                    aria-label="Chiudi modale"
                                    onClick={onCancelClickCb}
                            >
                                <span className="text-2xl leading-none">&times;</span>
                            </button>

                            <div className="border-b border-slate-800 px-4 py-3">
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                    <div className="flex items-start gap-4">
                                        <img
                                                alt="Live"
                                                src="/static/img/logo_icon.svg"
                                                className="mt-0.5 h-12 w-12 shrink-0 rounded-lg bg-sky-500/20 p-2 object-contain"
                                        />

                                        <div className="space-y-2">
                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                                                <h2 className="text-lg font-bold tracking-tight text-white">{(event?.name || '').substring(0, 80)}</h2>
                                                <span className="text-base font-bold text-emerald-400">{minute}</span>
                                                <span className="text-base font-bold text-white">{score}</span>
                                                <span className="text-sm font-semibold text-sky-400">ID: {event?.id}</span>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-300">
                                                <span className="inline-flex items-center gap-2">
                                                    <svg
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            className="h-4 w-4 text-slate-400"
                                                    >
                                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                                        <line x1="16" y1="2" x2="16" y2="6"/>
                                                        <line x1="8" y1="2" x2="8" y2="6"/>
                                                        <line x1="3" y1="10" x2="21" y2="10"/>
                                                    </svg>
                                                    <span>{datePart}</span>
                                                </span>
                                                <span>&bull;</span>
                                                <span>{timePart}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="overflow-x-auto dropdown-hidden-scrollbar">
                                <ModalLiveTable
                                        odds={books?.bookmakers || {}}
                                        event={event}
                                        highlightBest={highlightBest}
                                        refs={event?.id ? betfairRefs[event.id] : undefined}
                                />
                            </div>

                            <div className="flex flex-col items-center gap-3 px-4 py-5 text-xs text-slate-300">
                                <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/30 bg-slate-900/70 px-5 py-2">
                                    <span className="text-sky-400">★</span>
                                    <span>Prima riga (Betfair) = Book di riferimento mercato</span>
                                </div>

                                <label className="inline-flex items-center gap-3">
                                    <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-sky-500 bg-slate-950 text-sky-500"
                                            checked={highlightBest}
                                            onChange={(e) => setHighlightBest(e.target.checked)}
                                    />
                                    <span>Quote migliori evidenziate</span>
                                </label>
                            </div>
                        </div>
                    </div>
            )) ||
            null
    )
};
export default EventLiveDetailsModal;
